import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { AuthShell, Button, Stack, Typography } from '@platform/design-system';
import { PersonalSignUpScreen, RouterLink, useSession } from '@platform/app-kit';
import { AUTH_FRAME } from '../shell/brand.ts';
import { useDocumentTitle } from '../shell/document-title.ts';

/**
 * `/signup` — create the account (details → emailed code → signed in), then choose the plan on
 * `/subscribe`. The payment page's "cancel" lands here (`?slug=&cancelled=1`): the plan is picked
 * up again on `/subscribe`, after signing in if the session did not survive the round trip.
 */
export function SignupPage({ open }: { open: boolean }) {
  const { status, principal } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  useDocumentTitle('Create your account');

  if (params.get('cancelled') === '1') return <Navigate to="/subscribe?cancelled=1" replace />;
  if (status === 'authenticated')
    return <Navigate to={principal?.tenantId ? '/' : '/subscribe'} replace />;
  if (!open) return <SignupClosed />;

  return (
    <PersonalSignUpScreen
      frame={AUTH_FRAME}
      onSignedIn={() => void navigate('/subscribe', { replace: true })}
      onConfirmed={(email) =>
        void navigate(
          `/login?email=${encodeURIComponent(email)}&confirmed=1&returnTo=${encodeURIComponent('/subscribe')}`,
          { replace: true },
        )
      }
    />
  );
}

/**
 * `/signup/verify?email=` — the emailed code on its own (the sign-up tab was closed). The
 * password is not in memory here, so a confirmed person signs in next.
 */
export function VerifyEmailPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get('email');
  useDocumentTitle('Confirm your email');
  if (!email) return <Navigate to="/signup" replace />;
  return (
    <PersonalSignUpScreen
      frame={AUTH_FRAME}
      verifyEmail={email}
      onSignedIn={() => void navigate('/subscribe', { replace: true })}
      onConfirmed={(confirmed) =>
        void navigate(`/login?email=${encodeURIComponent(confirmed)}&confirmed=1`, {
          replace: true,
        })
      }
    />
  );
}

/** The edge does not reach the sign-up service yet (`controlPlane.platform` is off). */
function SignupClosed() {
  return (
    <AuthShell
      brandName={AUTH_FRAME.brandName}
      pitch={AUTH_FRAME.pitch}
      title="Sign-up opens soon"
      lead="New accounts can't be created here yet."
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Already have an account? You can still sign in.
        </Typography>
        <Button component={RouterLink} href="/login" variant="contained" size="large">
          Sign in
        </Button>
      </Stack>
    </AuthShell>
  );
}
